import React, {
	createContext,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useSyncExternalStore,
} from 'react';
import { useRecordStatusContext } from '@/context/house/documents/RecordStatusContext';
import { isEnrichmentPathInFlight } from '@/utils/pipelineStatusUtils';

/**
 * Bridges the gap between the status poll and the documents list. The "Processing" badge and the
 * Summary/Tags cards read enrichment status from the poll, but the actual tags/summary live on the
 * documents-list data, which only refreshes on a follow-up fetch. So when a doc's enrichment flips
 * done, the badge would clear on the poll tick while the tags/summary land a round-trip later —
 * leaving an empty gap.
 *
 * This tracker holds a "settling" flag per document from the moment its enrichment completes until the
 * documents refetch carrying its data resolves. It is per-document (not house-aggregate) so a batch
 * doesn't switch in lockstep, and it clears on the refetch — not on tags/summary being present — so
 * records that legitimately end with no tag/summary (notes, links, unclassified files) don't stick.
 *
 * Exposed as a subscribable store (not a plain context value) so consumers re-render only when *their*
 * document's flag flips, via useSyncExternalStore — a completion doesn't re-render the whole list.
 */
interface EnrichmentSettlingStore {
	subscribe: (onChange: () => void) => () => void;
	isSettling: (documentId?: number | null) => boolean;
}

const EnrichmentSettlingContext = createContext<EnrichmentSettlingStore | null>(null);

/** Debounce so a burst of completions triggers one documents refetch rather than one each. */
const SETTLE_FLUSH_DEBOUNCE_MS = 250;

export const EnrichmentSettlingProvider: React.FC<{
	refetchDocuments: () => Promise<unknown>;
	children: React.ReactNode;
}> = ({ refetchDocuments, children }) => {
	const { polledStatusById } = useRecordStatusContext();
	const settlingRef = useRef<Set<number>>(new Set());
	const subscribersRef = useRef<Set<() => void>>(new Set());
	const prevEnrichingRef = useRef<Set<number>>(new Set());
	const pendingFlushRef = useRef<Set<number>>(new Set());
	const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const refetchRef = useRef(refetchDocuments);
	refetchRef.current = refetchDocuments;

	// Stable store identity: the provider re-renders every poll, but consumers subscribe to the store,
	// not the context value, so they aren't re-rendered by the provider re-rendering.
	const store = useMemo<EnrichmentSettlingStore>(
		() => ({
			subscribe: (onChange) => {
				subscribersRef.current.add(onChange);
				return () => {
					subscribersRef.current.delete(onChange);
				};
			},
			isSettling: (documentId) => documentId != null && settlingRef.current.has(documentId),
		}),
		[],
	);

	const notify = () => subscribersRef.current.forEach((cb) => cb());

	// Layout effect (not effect) so the settling flag is applied before paint — otherwise the badge
	// would blink off for one frame between the status poll clearing it and this marking it settling.
	useLayoutEffect(() => {
		const currentEnriching = new Set<number>();
		for (const id in polledStatusById) {
			if (isEnrichmentPathInFlight(polledStatusById[Number(id)])) currentEnriching.add(Number(id));
		}

		const completed: number[] = [];
		prevEnrichingRef.current.forEach((id) => {
			if (!currentEnriching.has(id)) completed.push(id);
		});
		prevEnrichingRef.current = currentEnriching;

		if (completed.length === 0) return;

		completed.forEach((id) => {
			settlingRef.current.add(id);
			pendingFlushRef.current.add(id);
		});
		notify();

		if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
		flushTimerRef.current = setTimeout(() => {
			flushTimerRef.current = null;
			const snapshot = new Set(pendingFlushRef.current);
			pendingFlushRef.current = new Set();
			if (snapshot.size === 0) return;
			// The refetch started here begins after the completion, so its data is guaranteed to include
			// these docs; clear their settling flag once it resolves.
			void Promise.resolve(refetchRef.current()).then(() => {
				let changed = false;
				snapshot.forEach((id) => {
					if (settlingRef.current.delete(id)) changed = true;
				});
				if (changed) notify();
			});
		}, SETTLE_FLUSH_DEBOUNCE_MS);
	}, [polledStatusById]);

	useEffect(
		() => () => {
			if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
		},
		[],
	);

	return <EnrichmentSettlingContext.Provider value={store}>{children}</EnrichmentSettlingContext.Provider>;
};

const NOOP_SUBSCRIBE = () => () => {};

/**
 * True while the document's enrichment has completed but its refreshed tags/summary haven't landed yet.
 * Subscribes per-document, so it re-renders only when this doc's flag flips. Returns false outside the
 * provider (e.g. the drawer opened from chat), so consumers stay safe there.
 */
export const useIsEnrichmentSettling = (documentId?: number | null): boolean => {
	const store = useContext(EnrichmentSettlingContext);
	return useSyncExternalStore(store?.subscribe ?? NOOP_SUBSCRIBE, () =>
		store ? store.isSettling(documentId) : false,
	);
};
